import { useEffect, useState } from 'react';
import { FileText, AlertTriangle, CheckCircle, Loader2, DollarSign } from 'lucide-react';

interface InvoiceItem {
  id: string;
  product_name: string;
  quantity: number;
  declared_value_usd: number;
  total_declared_usd: number;
}

interface Invoice {
  id: string;
  status: 'draft' | 'confirmed';
  total_declared_usd: number;
  total_items: number;
  user_accepted_terms: boolean;
  confirmed_at?: string;
  invoice_items: InvoiceItem[];
}

interface InvoiceFormProps {
  checkoutToken: string;
  onConfirmed: () => void;
}

export default function InvoiceForm({ checkoutToken, onConfirmed }: InvoiceFormProps) {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [canEdit, setCanEdit] = useState(true);

  useEffect(() => {
    loadInvoice();
  }, []);

  const loadInvoice = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/api/checkout/${checkoutToken}/invoice`);
      const data = await res.json();

      if (data.success) {
        setInvoice(data.data.invoice);
        setItems(data.data.invoice.invoice_items || []);
        setCanEdit(data.data.can_edit);
        setTermsAccepted(data.data.invoice.user_accepted_terms || false);
      } else {
        alert(data.error || 'Erro ao carregar declaração aduaneira');
      }
    } catch (error) {
      console.error('Erro ao carregar invoice:', error);
      alert('Erro ao carregar declaração aduaneira');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (itemId: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setItems(items.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          declared_value_usd: numValue,
          total_declared_usd: numValue * item.quantity
        };
      }
      return item;
    }));
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + item.total_declared_usd, 0);
  };

  const handleSaveDraft = async () => {
    // Validar que todos os valores foram preenchidos
    const allFilled = items.every(item => item.declared_value_usd > 0);
    if (!allFilled) {
      alert('⚠️ Todos os produtos devem ter valores declarados maiores que zero');
      return;
    }

    setSaving(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const payload = {
        items: items.map(item => ({
          item_id: item.id,
          declared_value_usd: item.declared_value_usd
        })),
        user_accepted_terms: termsAccepted
      };

      const res = await fetch(`${apiUrl}/api/checkout/${checkoutToken}/invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (data.success) {
        alert('✅ Valores salvos com sucesso!');
        setInvoice(data.data.invoice);
      } else {
        alert(data.error || 'Erro ao salvar valores');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar valores');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!termsAccepted) {
      alert('⚠️ Você deve aceitar os termos da Receita Federal');
      return;
    }

    // Validar que todos os valores foram preenchidos
    const allFilled = items.every(item => item.declared_value_usd > 0);
    if (!allFilled) {
      alert('⚠️ Todos os produtos devem ter valores declarados maiores que zero');
      return;
    }

    const confirmed = window.confirm(
      '⚠️ ATENÇÃO: Depois de confirmar, você NÃO poderá mais alterar os valores declarados.\n\n' +
      'Esta declaração será enviada para a Receita Federal do Brasil.\n\n' +
      'Tem certeza de que os valores estão corretos?'
    );

    if (!confirmed) return;

    setConfirming(true);
    try {
      // Primeiro salvar valores
      await handleSaveDraft();

      // Depois confirmar
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/api/checkout/${checkoutToken}/invoice/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_accepted_terms: true })
      });

      const data = await res.json();

      if (data.success) {
        alert('✅ Declaração confirmada com sucesso!\n\nAgora você pode prosseguir para o pagamento.');
        setInvoice(data.data.invoice);
        setCanEdit(false);
        onConfirmed();
      } else {
        alert(data.error || 'Erro ao confirmar declaração');
      }
    } catch (error) {
      console.error('Erro ao confirmar:', error);
      alert('Erro ao confirmar declaração');
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-3 text-gray-600">Carregando declaração aduaneira...</span>
        </div>
      </div>
    );
  }

  // Se já foi confirmada, mostrar resumo
  if (invoice?.status === 'confirmed') {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <CheckCircle className="h-6 w-6 text-green-500 mr-2" />
          <h3 className="text-xl font-bold text-gray-900">Declaração Confirmada</h3>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <p className="text-green-800 font-medium">
            ✅ Sua declaração aduaneira foi confirmada com sucesso!
          </p>
          <p className="text-sm text-green-700 mt-2">
            Total Declarado: <span className="font-bold">USD {invoice.total_declared_usd?.toFixed(2)}</span>
          </p>
          {invoice.confirmed_at && (
            <p className="text-xs text-green-600 mt-1">
              Confirmado em: {new Date(invoice.confirmed_at).toLocaleString('pt-BR')}
            </p>
          )}
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            ⚠️ Esta declaração NÃO pode mais ser alterada. 
            Os valores serão enviados para a Receita Federal do Brasil junto com sua compra.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center mb-4">
        <FileText className="h-6 w-6 text-blue-500 mr-2" />
        <h3 className="text-xl font-bold text-gray-900">Declaração Aduaneira (Obrigatório)</h3>
      </div>

      {/* Aviso importante */}
      <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
        <div className="flex">
          <AlertTriangle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-red-800 font-bold">ATENÇÃO: Receita Federal do Brasil</h4>
            <p className="text-sm text-red-700 mt-1">
              Você está importando produtos dos Estados Unidos. É OBRIGATÓRIO declarar o valor REAL 
              que você pagou por cada produto.
            </p>
            <ul className="text-sm text-red-700 mt-2 space-y-1 list-disc list-inside">
              <li>Declare valores verdadeiros</li>
              <li>Valores incorretos podem gerar multas e retenção na alfândega</li>
              <li>Esta declaração é IRREVERSÍVEL após confirmação</li>
              <li>Você é responsável pela veracidade das informações</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Tabela de produtos */}
      <div className="overflow-x-auto mb-6">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Produto</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Qtd</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Valor Unit. (USD) *</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Total USD</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {items.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">{item.product_name}</td>
                <td className="px-4 py-3 text-center text-sm text-gray-600">{item.quantity}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end">
                    <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={item.declared_value_usd || ''}
                      onChange={(e) => handleValueChange(item.id, e.target.value)}
                      placeholder="Ex: 100.50"
                      disabled={!canEdit}
                      className="w-28 border border-gray-300 rounded px-2 py-1 text-right text-sm disabled:bg-gray-100"
                    />
                  </div>
                </td>
                <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                  $ {item.total_declared_usd.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-100 border-t-2 border-gray-300">
            <tr>
              <td colSpan={3} className="px-4 py-3 text-right text-base font-bold text-gray-900">
                Total Declarado:
              </td>
              <td className="px-4 py-3 text-right text-base font-bold text-blue-600">
                USD {calculateTotal().toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Checkbox de termos */}
      <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 mb-6">
        <label className="flex items-start cursor-pointer">
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            disabled={!canEdit}
            className="mt-1 h-5 w-5 text-blue-500 border-gray-300 rounded focus:ring-blue-500 disabled:bg-gray-100"
          />
          <span className="ml-3 text-sm text-gray-700">
            Eu declaro que os valores acima são <strong>verdadeiros e corretos</strong>, e estou ciente 
            das implicações legais de declaração falsa junto à Receita Federal do Brasil. 
            Confirmo que li e entendi todos os avisos apresentados.
          </span>
        </label>
      </div>

      {/* Botões */}
      {canEdit && (
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={saving || confirming}
            className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {saving ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar Rascunho'
            )}
          </button>

          <button
            type="button"
            onClick={handleConfirm}
            disabled={!termsAccepted || saving || confirming}
            className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {confirming ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Confirmando...
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5 mr-2" />
                Confirmar Declaração (Irreversível)
              </>
            )}
          </button>
        </div>
      )}

      <p className="text-xs text-gray-500 text-center mt-4">
        * Todos os valores devem ser declarados em Dólares Americanos (USD)
      </p>
    </div>
  );
}
